import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useUIStore } from '@/stores/uiStore';
import { useWorkspaceStore, selectCapabilitiesList, selectActiveWorkspace } from '@/stores/workspaceStore';
import { searchCapabilitiesLocally } from '@/lib/db';
import type { Capability } from '@/types';

// ─── Command types ────────────────────────────────────────────────────────────

interface Command {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  category: 'navigation' | 'action' | 'capability';
  action: () => void;
  keywords?: string[];
}

const NavIcon = ({ d }: { d: string }) => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d={d} />
  </svg>
);

// ─── CommandPalette ───────────────────────────────────────────────────────────

const CommandPalette: React.FC = () => {
  const history = useHistory();
  const closeCommandPalette = useUIStore((s) => s.closeCommandPalette);
  const openQuickCapture = useUIStore((s) => s.openQuickCapture);
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const capabilities = useWorkspaceStore(selectCapabilitiesList);

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [capabilityResults, setCapabilityResults] = useState<Capability[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useCallback(
    (path: string) => {
      history.push(path);
      closeCommandPalette();
    },
    [history, closeCommandPalette]
  );

  // Static commands
  const staticCommands: Command[] = [
    {
      id: 'nav-dashboard',
      label: 'Go to Dashboard',
      icon: <NavIcon d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
      category: 'navigation',
      action: () => navigate('/dashboard'),
      keywords: ['home', 'start'],
    },
    {
      id: 'nav-graph',
      label: 'Open Graph View',
      icon: <NavIcon d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0" />,
      category: 'navigation',
      action: () => navigate('/graph'),
      keywords: ['network', 'nodes', 'edges', 'map'],
    },
    {
      id: 'nav-capabilities',
      label: 'All Capabilities',
      icon: <NavIcon d="M9 11l3 3L22 4" />,
      category: 'navigation',
      action: () => navigate('/capabilities'),
      keywords: ['skills', 'list'],
    },
    {
      id: 'nav-collections',
      label: 'Collections',
      icon: <NavIcon d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />,
      category: 'navigation',
      action: () => navigate('/collections'),
      keywords: ['folders', 'groups'],
    },
    {
      id: 'nav-search',
      label: 'Semantic Search',
      icon: <NavIcon d="M21 21l-4.35-4.35M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0-16 0" />,
      category: 'navigation',
      action: () => navigate('/search'),
      keywords: ['find', 'ai'],
    },
    {
      id: 'nav-settings',
      label: 'Settings',
      icon: <NavIcon d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />,
      category: 'navigation',
      action: () => navigate('/settings'),
      keywords: ['preferences', 'account'],
    },
    {
      id: 'action-quick-capture',
      label: 'Quick Capture',
      description: 'Add a new capability instantly',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      ),
      category: 'action',
      action: () => {
        closeCommandPalette();
        openQuickCapture();
      },
      keywords: ['add', 'new', 'create', 'capture'],
    },
    {
      id: 'action-new-search',
      label: 'Search capabilities',
      description: 'Open search page with your query',
      icon: <NavIcon d="M21 21l-4.35-4.35M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0-16 0" />,
      category: 'action',
      action: () => navigate(`/search${query ? `?q=${encodeURIComponent(query)}` : ''}`),
      keywords: ['search', 'find', 'lookup'],
    },
  ];

  // Filter static commands
  const filteredCommands = query.trim()
    ? staticCommands.filter((cmd) => {
        const q = query.toLowerCase();
        return (
          cmd.label.toLowerCase().includes(q) ||
          cmd.description?.toLowerCase().includes(q) ||
          cmd.keywords?.some((k) => k.includes(q))
        );
      })
    : staticCommands;

  // Dynamic capability results
  useEffect(() => {
    if (!activeWorkspace?.id || query.trim().length < 2) {
      setCapabilityResults([]);
      return;
    }

    let cancelled = false;
    searchCapabilitiesLocally(activeWorkspace.id, query)
      .then((results) => {
        if (!cancelled) {
          setCapabilityResults(results.slice(0, 5));
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Fallback to in-memory search
          const q = query.toLowerCase();
          setCapabilityResults(
            capabilities
              .filter((c) => c.title.toLowerCase().includes(q) || c.tags.some((t) => t.includes(q)))
              .slice(0, 5)
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [query, activeWorkspace?.id, capabilities]);

  // Build combined results
  const capabilityCommands: Command[] = capabilityResults.map((cap) => ({
    id: `cap-${cap.id}`,
    label: cap.title,
    description: cap.type,
    icon: <svg className="w-4 h-4 text-accent-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="12" cy="12" r="3"/><circle cx="5" cy="6" r="2"/><circle cx="19" cy="6" r="2"/><line x1="12" y1="12" x2="5" y2="6"/><line x1="12" y1="12" x2="19" y2="6"/></svg>,
    category: 'capability' as const,
    action: () => navigate(`/capabilities/${cap.id}`),
  }));

  const allResults = [...filteredCommands, ...capabilityCommands];

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, allResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        allResults[selectedIndex]?.action();
      } else if (e.key === 'Escape') {
        closeCommandPalette();
      }
    },
    [allResults, selectedIndex, closeCommandPalette]
  );

  const categoryLabels: Record<string, string> = {
    navigation: 'Navigation',
    action: 'Actions',
    capability: 'Capabilities',
  };

  let lastCategory = '';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeCommandPalette}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-xl bg-white dark:bg-surface-900 rounded-2xl shadow-panel border border-surface-200 dark:border-surface-800 overflow-hidden animate-slide-in-up">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-200 dark:border-surface-800">
          <svg className="w-5 h-5 text-surface-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search or type a command…"
            className="flex-1 bg-transparent text-sm text-surface-900 dark:text-white placeholder:text-surface-400 outline-none"
            aria-label="Command palette search"
          />
          <kbd className="px-1.5 py-0.5 text-xs bg-surface-100 dark:bg-surface-800 rounded border border-surface-200 dark:border-surface-700 text-surface-400">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto py-2" role="listbox">
          {allResults.length === 0 ? (
            <p className="px-4 py-3 text-sm text-surface-400 text-center">No results found</p>
          ) : (
            allResults.map((cmd, i) => {
              const showHeader = cmd.category !== lastCategory;
              lastCategory = cmd.category;

              return (
                <React.Fragment key={cmd.id}>
                  {showHeader && (
                    <p className="px-4 py-1 text-2xs font-semibold text-surface-400 uppercase tracking-wide">
                      {categoryLabels[cmd.category] ?? cmd.category}
                    </p>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === selectedIndex}
                    onMouseEnter={() => setSelectedIndex(i)}
                    onClick={() => cmd.action()}
                    className={[
                      'w-full flex items-center gap-3 px-4 py-2 text-left transition-colors',
                      i === selectedIndex
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800',
                    ].join(' ')}
                  >
                    <span className={`flex-shrink-0 ${i === selectedIndex ? 'text-primary-600 dark:text-primary-300' : 'text-surface-400'}`}>
                      {cmd.icon}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="text-sm font-medium block truncate">{cmd.label}</span>
                      {cmd.description && (
                        <span className="text-xs text-surface-400 block truncate">{cmd.description}</span>
                      )}
                    </span>
                    {i === selectedIndex && (
                      <kbd className="flex-shrink-0 px-1 py-0.5 text-2xs bg-primary-100 dark:bg-primary-900/50 rounded text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800">
                        ↵
                      </kbd>
                    )}
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-surface-200 dark:border-surface-800 flex items-center gap-4 text-2xs text-surface-400">
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>ESC close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
